// src/services/emailService.js
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Service for handling all email communications
 */
class EmailService {
  constructor() {
    // Get Firebase functions instance
    this.functions = getFunctions();
    
    // Initialize callable functions
    this.sendConfirmationEmail = httpsCallable(this.functions, 'sendBookingConfirmation');
    this.sendAdminNotification = httpsCallable(this.functions, 'sendAdminBookingNotification');
    this.sendInvoiceEmail = httpsCallable(this.functions, 'sendInvoice');
    this.sendCartReminder = httpsCallable(this.functions, 'sendCartReminder');
    this.testOutlookEmail = httpsCallable(this.functions, 'testOutlookEmail');

    // Microsoft Graph email functions
    this.sendWelcomeEmail = httpsCallable(this.functions, 'sendWelcomeEmail');
    this.sendPurchaseConfirmation = httpsCallable(this.functions, 'sendPurchaseConfirmation');
    this.testMSGraphEmail = httpsCallable(this.functions, 'testMSGraphEmail');

    // Email Auto-Responder functions
    this.triggerEmailMonitoring = httpsCallable(this.functions, 'triggerEmailMonitoring');
    this.testAutoResponder = httpsCallable(this.functions, 'testAutoResponder');
    this.getLeads = httpsCallable(this.functions, 'getLeads');

    // Form submission functions
    this.submitFormToBookings = httpsCallable(this.functions, 'submitFormToBookings');
  }
  
  /**
   * Send booking confirmation email to customer
   * 
   * @param {Object} bookingData - Complete booking information
   * @returns {Promise} - Result of the email send operation
   */
  async sendBookingConfirmation(bookingData) {
    try {
      // Extract essential data
      const emailData = {
        recipientEmail: bookingData.guestEmail || bookingData.contactEmail || bookingData.customer?.email,
        bookingId: bookingData.id,
        serviceName: bookingData.service?.title,
        serviceDate: bookingData.schedule?.date,
        serviceTime: bookingData.schedule?.time,
        customerName: bookingData.customerName || bookingData.customer?.name || 'Valued Customer',
        vehicleInfo: bookingData.vehicle ? 
          `${bookingData.vehicle.year} ${bookingData.vehicle.make} ${bookingData.vehicle.model}` : 
          'Your vehicle',
        serviceLocation: bookingData.vehicle?.location || bookingData.location || '',
        paymentAmount: bookingData.payment?.total,
        specialInstructions: bookingData.vehicle?.instructions || bookingData.instructions || ''
      };
      
      console.log('Sending confirmation email to customer:', emailData.recipientEmail);
      const result = await this.sendConfirmationEmail(emailData);
      return result.data;
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      throw error;
    }
  }
  
  /**
   * Send new booking notification to administrators
   * 
   * @param {Object} bookingData - Complete booking information
   * @returns {Promise} - Result of the email send operation
   */
  async sendAdminNotification(bookingData) {
    try {
      // Process booking data for admin notification
      const adminData = {
        bookingId: bookingData.id,
        serviceName: bookingData.service?.title,
        serviceDate: bookingData.schedule?.date,
        serviceTime: bookingData.schedule?.time,
        customerName: bookingData.customerName || bookingData.customer?.name || 'Guest Customer',
        customerEmail: bookingData.guestEmail || bookingData.contactEmail || bookingData.customer?.email,
        customerPhone: bookingData.guestPhone || bookingData.contactPhone || bookingData.customer?.phone,
        vehicleInfo: bookingData.vehicle ? 
          `${bookingData.vehicle.year} ${bookingData.vehicle.make} ${bookingData.vehicle.model}` : 
          'Not specified',
        vehicleColor: bookingData.vehicle?.color || 'Not specified',
        vehicleLocation: bookingData.vehicle?.location || bookingData.location || '',
        paymentAmount: bookingData.payment?.total,
        paymentMethod: bookingData.payment?.method || 'Online payment',
        specialInstructions: bookingData.vehicle?.instructions || bookingData.instructions || '',
        isNewCustomer: bookingData.isGuest || !bookingData.customer?.id
      };
      
      console.log('Sending booking notification to administrators');
      const result = await this.sendAdminNotification(adminData);
      return result.data;
    } catch (error) {
      console.error('Error sending admin notification:', error);
      throw error;
    }
  }

  /**
   * Send invoice email to customer
   * 
   * @param {Object} invoiceData - Complete invoice information
   * @returns {Promise} - Result of the email send operation
   */
  async sendInvoice(invoiceData) {
    try {
      const data = {
        recipientEmail: invoiceData.customerEmail,
        customerName: invoiceData.customerName,
        invoiceData: {
          invoiceNumber: invoiceData.invoiceNumber,
          date: invoiceData.date,
          dueDate: invoiceData.dueDate,
          items: invoiceData.items,
          subtotal: invoiceData.subtotal,
          taxRate: invoiceData.taxRate,
          tax: invoiceData.tax,
          total: invoiceData.total,
          paymentStatus: invoiceData.paymentStatus
        }
      };

      console.log('Sending invoice to customer:', data.recipientEmail);
      const result = await this.sendInvoiceEmail(data);
      return result.data;
    } catch (error) {
      console.error('Error sending invoice:', error);
      throw error;
    }
  }

  /**
   * Send cart reminder email to customer
   * 
   * @param {Object} cartData - Cart information
   * @returns {Promise} - Result of the email send operation
   */
  async sendCartReminder(cartData) {
    try {
      const data = {
        recipientEmail: cartData.customerEmail,
        customerName: cartData.customerName,
        cartItems: cartData.items,
        cartTotal: cartData.total,
        bookingUrl: cartData.bookingUrl
      };

      console.log('Sending cart reminder to customer:', data.recipientEmail);
      const result = await this.sendCartReminder(data);
      return result.data;
    } catch (error) {
      console.error('Error sending cart reminder:', error);
      throw error;
    }
  }
  
  /**
   * Send both customer confirmation and admin notification emails
   * 
   * @param {Object} bookingData - Complete booking information
   * @returns {Promise} - Results from both email operations
   */
  async sendAllBookingEmails(bookingData) {
    try {
      const customerEmailPromise = this.sendBookingConfirmation(bookingData);
      const adminEmailPromise = this.sendAdminNotification(bookingData);
      
      const [customerResult, adminResult] = await Promise.all([
        customerEmailPromise,
        adminEmailPromise
      ]);
      
      return {
        customerEmail: customerResult,
        adminEmail: adminResult
      };
    } catch (error) {
      console.error('Error sending booking emails:', error);
      // Don't stop execution if emails fail - just log the error
      return {
        error: error.message
      };
    }
  }

  /**
   * Test email delivery to Outlook 365
   *
   * @param {string} testEmail - The Outlook 365 email to test
   * @returns {Promise} - Result of the test email operation
   */
  async testOutlook365(testEmail) {
    try {
      console.log('Testing Outlook 365 email delivery to:', testEmail);
      const result = await this.testOutlookEmail({ testEmail });
      return result.data;
    } catch (error) {
      console.error('Error testing Outlook 365 email:', error);
      throw error;
    }
  }

  /**
   * Send welcome email to new user via Microsoft Graph
   *
   * @param {Object} userData - User registration data
   * @returns {Promise} - Result of the welcome email operation
   */
  async sendUserWelcomeEmail(userData) {
    try {
      const emailData = {
        recipientEmail: userData.email,
        firstName: userData.firstName,
        bookingUrl: window.location.origin + '/booking'
      };

      console.log('Sending welcome email via MS Graph to:', emailData.recipientEmail);
      const result = await this.sendWelcomeEmail(emailData);
      return result.data;
    } catch (error) {
      console.error('Error sending welcome email via MS Graph:', error);
      throw error;
    }
  }

  /**
   * Send purchase confirmation email via Microsoft Graph
   *
   * @param {Object} purchaseData - Purchase/order information
   * @returns {Promise} - Result of the purchase confirmation operation
   */
  async sendOrderConfirmation(purchaseData) {
    try {
      const emailData = {
        recipientEmail: purchaseData.customerEmail,
        orderId: purchaseData.orderId || purchaseData.bookingId || purchaseData.id,
        items: purchaseData.items || [{
          name: purchaseData.serviceName || 'Auto Detailing Service',
          price: parseFloat(purchaseData.total || purchaseData.payment?.total || 0),
          quantity: 1
        }],
        subtotal: parseFloat(purchaseData.subtotal || purchaseData.total || purchaseData.payment?.total || 0),
        tax: purchaseData.tax ? parseFloat(purchaseData.tax) : undefined,
        discount: purchaseData.discount ? parseFloat(purchaseData.discount) : undefined,
        total: parseFloat(purchaseData.total || purchaseData.payment?.total || 0),
        serviceDate: purchaseData.serviceDate || purchaseData.schedule?.date,
        serviceTime: purchaseData.serviceTime || purchaseData.schedule?.time,
        location: purchaseData.location || purchaseData.vehicle?.location,
        vehicle: purchaseData.vehicle ?
          `${purchaseData.vehicle.year} ${purchaseData.vehicle.make} ${purchaseData.vehicle.model}` :
          undefined,
        paymentMethod: purchaseData.paymentMethod || purchaseData.payment?.method || 'Credit Card',
        transactionId: purchaseData.transactionId || purchaseData.payment?.transactionId || purchaseData.payment_intent,
        specialInstructions: purchaseData.specialInstructions || purchaseData.vehicle?.instructions
      };

      console.log('Sending purchase confirmation via MS Graph to:', emailData.recipientEmail);
      const result = await this.sendPurchaseConfirmation(emailData);
      return result.data;
    } catch (error) {
      console.error('Error sending purchase confirmation via MS Graph:', error);
      throw error;
    }
  }

  /**
   * Test Microsoft Graph email functionality
   *
   * @param {string} testEmail - The email to test
   * @returns {Promise} - Result of the test email operation
   */
  async testMicrosoftGraph(testEmail) {
    try {
      console.log('Testing Microsoft Graph email delivery to:', testEmail);
      const result = await this.testMSGraphEmail({ testEmail });
      return result.data;
    } catch (error) {
      console.error('Error testing Microsoft Graph email:', error);
      throw error;
    }
  }

  /**
   * Test auto-responder email template
   *
   * @param {Object} testData - Test configuration
   * @returns {Promise} - Result of the test
   */
  async testAutoResponderEmail(testData) {
    try {
      console.log('Testing auto-responder email to:', testData.testEmail);
      const result = await this.testAutoResponder(testData);
      return result.data;
    } catch (error) {
      console.error('Error testing auto-responder email:', error);
      throw error;
    }
  }

  /**
   * Manually trigger email monitoring (for testing)
   *
   * @returns {Promise} - Result of the monitoring
   */
  async triggerInboxMonitoring() {
    try {
      console.log('Manually triggering email inbox monitoring...');
      const result = await this.triggerEmailMonitoring();
      return result.data;
    } catch (error) {
      console.error('Error triggering email monitoring:', error);
      throw error;
    }
  }

  /**
   * Get leads from the database
   *
   * @returns {Promise} - Array of leads
   */
  async getEmailLeads() {
    try {
      console.log('Fetching email leads from database...');
      const result = await this.getLeads();
      return result.data;
    } catch (error) {
      console.error('Error fetching email leads:', error);
      throw error;
    }
  }

  /**
   * Submit form data to bookings@jp-autodetailing.com
   *
   * @param {string} formType - Type of form (contact, dealership, guest-vehicle, etc.)
   * @param {Object} formData - Form data to submit
   * @param {string} section - Website section where form was submitted
   * @returns {Promise} - Result of the form submission
   */
  async sendFormToBookings(formType, formData, section = 'website') {
    try {
      console.log('Submitting form to bookings email:', {
        formType,
        name: formData.name || formData.contactName,
        section
      });

      const result = await this.submitFormToBookings({
        formType,
        formData,
        section
      });

      console.log('Form submitted successfully:', result.data);
      return result.data;
    } catch (error) {
      console.error('Error submitting form to bookings:', error);
      throw error;
    }
  }
}

// Create singleton instance
const emailService = new EmailService();

export default emailService;